import { getModuleName, getModuleColor } from '../../constants/licenses';
import { Badge } from '../common';

/**
 * ModuleBadges - Displays allowed modules for a license
 *
 * @param {Array<number>} modules - Array of BuildingModuleType enum values
 * @param {string} variant - Display variant: 'compact' (count only), 'badges' (show all badges), 'list' (vertical list)
 * @param {number} maxVisible - Maximum number of badges to show before "+X more" (only for 'badges' variant)
 */
function ModuleBadges({ modules = [], variant = 'compact', maxVisible = 3 }) {
  if (!modules || modules.length === 0) {
    return <span className="text-sm text-gray-500">No modules</span>;
  }

  // Compact variant: just show count
  if (variant === 'compact') {
    return (
      <span className="text-sm font-medium text-gray-700">
        {modules.length} {modules.length === 1 ? 'module' : 'modules'}
      </span>
    );
  }

  // List variant: vertical list of all modules
  if (variant === 'list') {
    return (
      <div className="space-y-2">
        {modules.map((module) => (
          <div key={module} className="flex items-center">
            <Badge variant="custom" className={`${getModuleColor(module)} text-xs`}>
              {getModuleName(module)}
            </Badge>
          </div>
        ))}
      </div>
    );
  }

  // Badges variant: horizontal badges with optional "more" indicator
  const visibleModules = modules.slice(0, maxVisible);
  const remainingCount = modules.length - maxVisible;

  return (
    <div className="flex flex-wrap gap-1">
      {visibleModules.map((module) => (
        <Badge key={module} variant="custom" className={`${getModuleColor(module)} text-xs whitespace-nowrap`}>
          {getModuleName(module)}
        </Badge>
      ))}
      {remainingCount > 0 && (
        <Badge variant="custom" className="bg-gray-100 text-gray-600 text-xs">
          +{remainingCount} more
        </Badge>
      )}
    </div>
  );
}

export default ModuleBadges;
